import { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import FormControl from "@mui/material/FormControl";
import EmailIcon from "@mui/icons-material/Email";
import { motion, AnimatePresence } from "framer-motion";
import { db, doc, updateDoc } from "../../../../firebaseConfig";

import { useAtom } from "jotai";
import { themeAtom, isAdminAtom } from "../../../../-Atoms";

const ConfirmationStatus = ({
  jobData,
  jobClientData,
  confirmationStatusEmailOpen,
  setConfirmationStatusEmailOpen,
  firstConfirmationStatusEmailOpen,
  setFirstConfirmationStatusEmailOpen,
  setConfirmationEmailData,
}) => {
  const [theme] = useAtom(themeAtom);
  const [isAdmin] = useAtom(isAdminAtom);

  const [confirmationEmailSent, setConfirmationEmailSent] = useState(false);
  useEffect(() => {
    setConfirmationEmailSent(jobData.confirmationEmailSent);
  }, [jobData]);

  const [confirmationStatus, setConfirmationStatus] = useState(
    jobData.confirmationStatus
  );
  useEffect(() => {
    setConfirmationStatus(jobData.confirmationStatus);
  }, [jobData]);

  const [sendConfirmationEmailButtonText, setSendConfirmationEmailButtonText] =
    useState("Send Confirmed Email");

  useEffect(() => {
    setSendConfirmationEmailButtonText(
      confirmationEmailSent ? "Confirmed Email Sent" : "Send Confirmed Email"
    );
  }, [confirmationEmailSent]);

  useEffect(() => {
    const keyDownHandler = (event) => {
      if (event.key === "Alt") {
        event.preventDefault();
        setConfirmationEmailSent(!confirmationStatus);
      }
    };
    const keyUpHandler = (event) => {
      if (event.key === "Alt") {
        event.preventDefault();
        setConfirmationEmailSent(confirmationStatus);
      }
    };

    document.addEventListener("keydown", keyDownHandler);
    document.addEventListener("keyup", keyUpHandler);

    return () => {
      document.removeEventListener("keydown", keyDownHandler);
      document.removeEventListener("keydown", keyUpHandler);
    };
  }, [setConfirmationEmailSent, confirmationEmailSent]);

  const itemVariants = {
    hidden: {
      opacity: 0,
      transition: {
        duration: 0.2,
        ease: "easeInOut",
      },
    },
    visible: (i) => ({
      opacity: 1,
      transition: {
        delay: i * 0.03,
        duration: 0.2,
        ease: "easeInOut",
      },
    }),
    hover: {
      scale: 1.02,
      transition: {
        duration: 0.15,
        ease: "easeInOut",
      },
    },
    tap: {
      scale: 0.98,
      transition: {
        duration: 0.1,
        ease: "easeInOut",
      },
    },
  };

  const emailButtonVariants = {
    initial: {
      opacity: 0,
      transition: {
        duration: 0.2,
        ease: "easeInOut",
      },
    },
    animate: {
      opacity: 1,
      transition: {
        duration: 0.2,
        ease: "easeInOut",
      },
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.15,
        ease: "easeInOut",
      },
    },
    hover: {
      scale: 1.02,
      transition: {
        duration: 0.15,
        ease: "easeInOut",
      },
    },
  };

  const containerVariants = {
    hidden: {
      opacity: 0,
      transition: {
        duration: 0.2,
        ease: "easeInOut",
      },
    },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.2,
        ease: "easeInOut",
        staggerChildren: 0.03,
      },
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.15,
        ease: "easeInOut",
      },
    },
  };

  const getStatusColor = (status) => {
    if (status === "confirmed") return theme === "dark" ? "#4CAF50" : "#2E7D32";
    if (status === "pending") return theme === "dark" ? "#FF9800" : "#ED6C02";
    return theme === "dark" ? "#F44336" : "#D32F2F";
  };

  const filterButtonStyles = {
    fontWeight: "bold",
    padding: "8px 12px",
    minWidth: { xs: "auto", sm: "120px" },
    maxWidth: { xs: "100px", sm: "none" },
    border: `1px solid ${
      theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
    } !important`,
    borderRadius: "8px !important",
    fontSize: { xs: 12, sm: 13 },
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    textTransform: "none",
    boxShadow:
      theme === "dark"
        ? "0 2px 5px rgba(0,0,0,0.2)"
        : "0 2px 5px rgba(0,0,0,0.05)",
    whiteSpace: "nowrap",
  };

  const handleConfirmationStatusChange = (event, newValue) => {
    if (!isAdmin) return;

    if (newValue === null || typeof newValue === "undefined") {
      console.warn(
        "Confirmation status change attempted with invalid value:",
        newValue
      );
      return;
    }
    setConfirmationStatus(newValue);
    updateDoc(doc(db, "JobData", jobData.id), { confirmationStatus: newValue });
  };
  return (
    <FormControl
      fullWidth
      sx={{
        marginTop: 2,
        position: "relative",
        "& .MuiInputLabel-root": {
          color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
          fontWeight: 500,
          fontSize: "0.95rem",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        },
      }}
      variant="filled"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          padding: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <AnimatePresence>
            <motion.div
              key="toggle-button-group"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <ToggleButtonGroup
                value={confirmationStatus || "tbc"}
                exclusive
                onChange={handleConfirmationStatusChange}
                sx={{
                  display: "flex",
                  flexWrap: { xs: "nowrap", sm: "wrap" },
                  gap: { xs: 0.5, sm: 1 },
                  width: { xs: "100%", md: "auto" },
                  justifyContent: { xs: "space-between", sm: "space-between" },
                }}
              >
                <Tooltip title={!isAdmin ? "Admin access required" : ""} arrow>
                  <motion.div
                    whileHover={isAdmin ? "hover" : ""}
                    whileTap={isAdmin ? "tap" : ""}
                    variants={itemVariants}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.05 }}
                  >
                    <ToggleButton
                      value="confirmed"
                      sx={{
                        ...filterButtonStyles,
                        backgroundColor:
                          confirmationStatus === "confirmed"
                            ? `${getStatusColor("confirmed")}30`
                            : "transparent",
                        color:
                          confirmationStatus === "confirmed"
                            ? getStatusColor("confirmed")
                            : theme === "dark"
                            ? "rgba(255,255,255,0.7)"
                            : "rgba(0,0,0,0.7)",
                        borderColor:
                          confirmationStatus === "confirmed"
                            ? `${getStatusColor("confirmed")}80 !important`
                            : theme === "dark"
                            ? "rgba(255,255,255,0.1) !important"
                            : "rgba(0,0,0,0.1) !important",
                        "&:hover": {
                          backgroundColor:
                            confirmationStatus === "confirmed"
                              ? `${getStatusColor("confirmed")}40`
                              : theme === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.05)",
                        },
                        flex: { xs: 1, sm: "none" },
                      }}
                      disabled={!isAdmin}
                    >
                      <Box sx={{ whiteSpace: "nowrap" }}>Confirmed</Box>
                    </ToggleButton>
                  </motion.div>
                </Tooltip>

                <Tooltip title={!isAdmin ? "Admin access required" : ""} arrow>
                  <motion.div
                    whileHover={isAdmin ? "hover" : ""}
                    whileTap={isAdmin ? "tap" : ""}
                    variants={itemVariants}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.05 }}
                  >
                    <ToggleButton
                      value="pending"
                      sx={{
                        ...filterButtonStyles,
                        backgroundColor:
                          confirmationStatus === "pending"
                            ? `${getStatusColor("pending")}30`
                            : "transparent",
                        color:
                          confirmationStatus === "pending"
                            ? getStatusColor("pending")
                            : theme === "dark"
                            ? "rgba(255,255,255,0.7)"
                            : "rgba(0,0,0,0.7)",
                        borderColor:
                          confirmationStatus === "pending"
                            ? `${getStatusColor("pending")}80 !important`
                            : theme === "dark"
                            ? "rgba(255,255,255,0.1) !important"
                            : "rgba(0,0,0,0.1) !important",
                        "&:hover": {
                          backgroundColor:
                            confirmationStatus === "pending"
                              ? `${getStatusColor("pending")}40`
                              : theme === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.05)",
                        },
                        flex: { xs: 1, sm: "none" },
                      }}
                      disabled={!isAdmin}
                    >
                      <Box sx={{ whiteSpace: "nowrap" }}>Pending</Box>
                    </ToggleButton>
                  </motion.div>
                </Tooltip>

                <Tooltip title={!isAdmin ? "Admin access required" : ""} arrow>
                  <motion.div
                    whileHover={isAdmin ? "hover" : ""}
                    whileTap={isAdmin ? "tap" : ""}
                    variants={itemVariants}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.05 }}
                  >
                    <ToggleButton
                      value="tbc"
                      sx={{
                        ...filterButtonStyles,
                        backgroundColor:
                          confirmationStatus === "tbc"
                            ? `${getStatusColor("tbc")}30`
                            : "transparent",
                        color:
                          confirmationStatus === "tbc"
                            ? getStatusColor("tbc")
                            : theme === "dark"
                            ? "rgba(255,255,255,0.7)"
                            : "rgba(0,0,0,0.7)",
                        borderColor:
                          confirmationStatus === "tbc"
                            ? `${getStatusColor("tbc")}80 !important`
                            : theme === "dark"
                            ? "rgba(255,255,255,0.1) !important"
                            : "rgba(0,0,0,0.1) !important",
                        "&:hover": {
                          backgroundColor:
                            confirmationStatus === "tbc"
                              ? `${getStatusColor("tbc")}40`
                              : theme === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(0,0,0,0.05)",
                        },
                        flex: { xs: 1, sm: "none" },
                      }}
                      disabled={!isAdmin}
                    >
                      <Box sx={{ whiteSpace: "nowrap" }}>Not Confirmed</Box>
                    </ToggleButton>
                  </motion.div>
                </Tooltip>
              </ToggleButtonGroup>
            </motion.div>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            {confirmationStatus === "confirmed" && (
              <motion.div
                key="confirmed-email-button"
                variants={emailButtonVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                whileHover="hover"
              >
                <Tooltip
                  title={
                    !isAdmin
                      ? "Admin access required"
                      : confirmationEmailSent
                      ? "Email already sent"
                      : "Send confirmation email"
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      disabled={confirmationEmailSent || !isAdmin}
                      startIcon={<EmailIcon />}
                      sx={{
                        backgroundColor: confirmationEmailSent
                          ? theme === "dark"
                            ? "rgba(100,100,100,0.3)"
                            : "rgba(200,200,200,0.5)"
                          : theme === "dark"
                          ? "rgba(100,100,255,0.8)"
                          : "rgba(100,100,255,0.9)",
                        color: theme === "dark" ? "#ffffff" : "#ffffff",
                        padding: "8px 16px",
                        borderRadius: "8px",
                        textTransform: "none",
                        fontSize: 13,
                        fontWeight: 500,
                        boxShadow:
                          theme === "dark"
                            ? "0 2px 8px rgba(0,0,0,0.3)"
                            : "0 2px 8px rgba(0,0,0,0.1)",
                        "&:hover": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(100,100,255,0.9)"
                              : "rgba(100,100,255,1)",
                          boxShadow:
                            theme === "dark"
                              ? "0 4px 12px rgba(0,0,0,0.4)"
                              : "0 4px 12px rgba(0,0,0,0.2)",
                        },
                        "&.Mui-disabled": {
                          color:
                            theme === "dark"
                              ? "rgba(255,255,255,0.3)"
                              : "rgba(0,0,0,0.3)",
                        },
                        width: { xs: "100%", sm: "100%", md: "200px" },
                      }}
                      onClick={(event) => {
                        if (!isAdmin) return;
                        setConfirmationEmailData({
                          jobData,
                          clientData: jobClientData,
                        });

                        setConfirmationStatusEmailOpen(
                          !confirmationStatusEmailOpen
                        );
                      }}
                    >
                      {sendConfirmationEmailButtonText}
                    </Button>
                  </span>
                </Tooltip>
              </motion.div>
            )}

            {confirmationStatus === "tbc" && (
              <motion.div
                key="tbc-email-button"
                variants={emailButtonVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                whileHover="hover"
              >
                <Tooltip
                  title={
                    !isAdmin
                      ? "Admin access required"
                      : "Send first confirmation email"
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      disabled={!isAdmin}
                      startIcon={<EmailIcon />}
                      sx={{
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(100,100,255,0.8)"
                            : "rgba(100,100,255,0.9)",
                        color: "white",
                        padding: "8px 16px",
                        borderRadius: "8px",
                        textTransform: "none",
                        fontSize: 12,
                        fontWeight: 500,
                        boxShadow:
                          theme === "dark"
                            ? "0 2px 8px rgba(0,0,0,0.3)"
                            : "0 2px 8px rgba(0,0,0,0.1)",
                        "&:hover": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(100,100,255,0.9)"
                              : "rgba(100,100,255,1)",
                          boxShadow:
                            theme === "dark"
                              ? "0 4px 12px rgba(0,0,0,0.4)"
                              : "0 4px 12px rgba(0,0,0,0.2)",
                        },
                        "&.Mui-disabled": {
                          color:
                            theme === "dark"
                              ? "rgba(255,255,255,0.3)"
                              : "rgba(0,0,0,0.3)",
                        },
                        width: { xs: "100%", sm: "100%", md: "230px" },
                      }}
                      onClick={(event) => {
                        if (!isAdmin) return;
                        setConfirmationEmailData({
                          jobData,
                          clientData: jobClientData,
                        });

                        setFirstConfirmationStatusEmailOpen(
                          !firstConfirmationStatusEmailOpen
                        );
                      }}
                    >
                      Send First Confirmation Email
                    </Button>
                  </span>
                </Tooltip>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Box>
    </FormControl>
  );
};

export default ConfirmationStatus;
